import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import FamiliesPage from './pages/FamiliesPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import FeeStructurePage from './pages/FeeStructurePage';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsPage from './pages/PaymentsPage';
import BankReconciliationPage from './pages/BankReconciliationPage';
import DiscountsPage from './pages/DiscountsPage';
import ExpensesPage from './pages/ExpensesPage';
import UniformsPage from './pages/UniformsPage';
import ReportsPage from './pages/ReportsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import FeedbackTicketsPage from './pages/FeedbackTicketsPage';
import ProfilePage from './pages/ProfilePage';
import TallySetupPage from './pages/TallySetupPage';
import RateMasterPage from './pages/RateMasterPage';
import CreditNotesPage from './pages/CreditNotesPage';
import LunchCouponPage from './pages/LunchCouponPage';
import ClubAttendancePage from './pages/ClubAttendancePage';

// Access Denied Component
const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center h-64 p-8">
    <div className="p-4 bg-[#C85A40]/10 rounded-full mb-4">
      <svg className="w-12 h-12 text-[#C85A40]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </div>
    <h2 className="text-xl font-bold text-[#1C1F1D] mb-2">Access Denied</h2>
    <p className="text-[#5C635F] text-center">You don't have permission to access this page.</p>
    <p className="text-sm text-[#5C635F] mt-2">Contact your administrator if you need access.</p>
  </div>
);

// Protected Route Component with page access check
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAF8]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A4331]"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Page Route Component with access control
const PageRoute = ({ pageKey, children }) => {
  const { hasPageAccess, loading, permissions } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3E3F96]"></div>
      </div>
    );
  }
  
  // Allow access if page key not specified or user has access
  if (!pageKey || hasPageAccess(pageKey)) {
    return children;
  }
  
  return <AccessDenied />;
};

// Smart redirect based on user role
const SmartRedirect = () => {
  const { permissions, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAF8]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A4331]"></div>
      </div>
    );
  }
  
  // Redirect to first available page based on role
  const pages = permissions?.pages || [];
  
  if (pages.includes('all') || pages.includes('dashboard')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (pages.includes('lunch_coupons')) {
    return <Navigate to="/lunch-coupons" replace />;
  }
  if (pages.includes('students')) {
    return <Navigate to="/students" replace />;
  }
  if (pages.includes('invoices')) {
    return <Navigate to="/invoices" replace />;
  }
  if (pages.includes('reports')) {
    return <Navigate to="/reports" replace />;
  }
  
  // Fallback to dashboard
  return <Navigate to="/dashboard" replace />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, permissions } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAF8]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A4331]"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Smart redirect based on role
    const pages = permissions?.pages || [];
    if (pages.includes('all') || pages.includes('dashboard')) {
      return <Navigate to="/dashboard" replace />;
    }
    if (pages.includes('lunch_coupons')) {
      return <Navigate to="/lunch-coupons" replace />;
    }
    if (pages.includes('students')) {
      return <Navigate to="/students" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SmartRedirect />} />
        <Route path="dashboard" element={<PageRoute pageKey="dashboard"><DashboardPage /></PageRoute>} />
        <Route path="families" element={<PageRoute pageKey="families"><FamiliesPage /></PageRoute>} />
        <Route path="students" element={<PageRoute pageKey="students"><StudentsPage /></PageRoute>} />
        <Route path="students/:id" element={<PageRoute pageKey="students"><StudentDetailPage /></PageRoute>} />
        <Route path="fee-structure" element={<PageRoute pageKey="fee_structure"><FeeStructurePage /></PageRoute>} />
        <Route path="invoices" element={<PageRoute pageKey="invoices"><InvoicesPage /></PageRoute>} />
        <Route path="payments" element={<PageRoute pageKey="payments"><PaymentsPage /></PageRoute>} />
        <Route path="bank-reconciliation" element={<PageRoute pageKey="bank_reconciliation"><BankReconciliationPage /></PageRoute>} />
        <Route path="discounts" element={<PageRoute pageKey="discounts"><DiscountsPage /></PageRoute>} />
        <Route path="expenses" element={<PageRoute pageKey="expenses"><ExpensesPage /></PageRoute>} />
        <Route path="credit-notes" element={<PageRoute pageKey="credit_notes"><CreditNotesPage /></PageRoute>} />
        <Route path="uniforms" element={<PageRoute pageKey="uniforms"><UniformsPage /></PageRoute>} />
        <Route path="lunch-coupons" element={<PageRoute pageKey="lunch_coupons"><LunchCouponPage /></PageRoute>} />
        <Route path="club-attendance" element={<PageRoute pageKey="club_attendance"><ClubAttendancePage /></PageRoute>} />
        <Route path="reports" element={<PageRoute pageKey="reports"><ReportsPage /></PageRoute>} />
        <Route path="users" element={<UsersPage />} />
        <Route path="settings" element={<PageRoute pageKey="settings"><SettingsPage /></PageRoute>} />
        <Route path="feedback-tickets" element={<PageRoute pageKey="feedback_tickets"><FeedbackTicketsPage /></PageRoute>} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="tally-setup" element={<TallySetupPage />} />
        <Route path="rate-master" element={<RateMasterPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<SmartRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
